/**
 * Law Firms - Dashboard Page
 * Overview metrics for law firms
 */

import { useEffect, useMemo } from 'react';
import { useIndustryCases } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Scale, Users, DollarSign, TrendingUp, Briefcase, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MainLayout } from '@/components/MainLayout';

export default function LawFirmsDashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard Jurídico | Controle Financeiro';
  }, []);

  const { cases, isLoading } = useIndustryCases({ domain: CompanyType.LAW_FIRMS });

  const stats = useMemo(() => {
    if (!cases) return null;

    const activeCases = cases.filter((c) => c.status === 'active').length;
    const wonCases = cases.filter((c) => c.status === 'won').length;
    const lostCases = cases.filter((c) => c.status === 'lost').length;
    const settledCases = cases.filter((c) => c.status === 'settled').length;

    // Aggregate by practice area
    const practiceAreaMap = new Map<string, number>();
    cases.forEach((c) => {
      const area = (c.metadata?.practiceArea as string) || 'Outros';
      practiceAreaMap.set(area, (practiceAreaMap.get(area) || 0) + 1);
    });

    const totalFees = cases.reduce((sum, c) => {
      const fee = (c.metadata?.fee as number) || 0;
      return sum + fee;
    }, 0);

    const avgFee = cases.length > 0 ? totalFees / cases.length : 0;
    const successRate = (wonCases + settledCases) > 0 ? ((wonCases + settledCases) / cases.length) * 100 : 0;

    return {
      totalCases: cases.length,
      activeCases,
      wonCases,
      lostCases,
      settledCases,
      practiceAreas: practiceAreaMap.size,
      totalFees,
      avgFee,
      successRate,
      topPracticeAreas: Array.from(practiceAreaMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3),
    };
  }, [cases]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    </MainLayout>
      );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Scale className="h-8 w-8" />
            Dashboard Jurídico
          </h1>
          <p className="text-muted-foreground">Visão geral dos casos e métricas do escritório</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Total de Casos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCases || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.activeCases || 0} ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Áreas de Atuação
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.practiceAreas || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.topPracticeAreas && stats.topPracticeAreas.length > 0
                ? `${stats.topPracticeAreas[0][0]} (${stats.topPracticeAreas[0][1]})`
                : 'Nenhuma área'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              Taxa de Sucesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.successRate.toFixed(0)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.wonCases || 0} ganhos · {stats?.settledCases || 0} acordos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Honorário Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.avgFee || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Valor médio por caso</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Receita Total em Honorários
          </CardTitle>
          <CardDescription>Soma de todos os casos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-600">{formatCurrency(stats?.totalFees || 0)}</div>
          <p className="text-sm text-muted-foreground mt-2">
            Receita gerada em {stats?.totalCases || 0} casos
          </p>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Casos</CardTitle>
            <CardDescription>Gerencie seu portfólio de casos</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/law-firms/cases">
              <Button className="w-full">
                <Briefcase className="mr-2 h-4 w-4" />
                Gerenciar Casos
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Relatórios</CardTitle>
            <CardDescription>Performance e estatísticas</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/law-firms/performance">
              <Button className="w-full" variant="outline">
                <Award className="mr-2 h-4 w-4" />
                Ver Performance
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Clientes</CardTitle>
            <CardDescription>Gestão de clientes</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/clients">
              <Button className="w-full" variant="outline">
                <Users className="mr-2 h-4 w-4" />
                Ver Clientes
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Top Practice Areas */}
      {stats && stats.topPracticeAreas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Principais Áreas de Atuação</CardTitle>
            <CardDescription>Áreas com mais casos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.topPracticeAreas.map(([area, count], index) => (
                <div key={area} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">#{index + 1}</div>
                    <div>{area}</div>
                  </div>
                  <div className="text-sm text-muted-foreground">{count} casos</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </MainLayout>
  );
}
